/** Filter panel. Every control writes the store, pushes the query and refetches through one function. */
import { useDebounceFn } from '@vueuse/core'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { CurriculumModuleOption } from './interfaces'
import type { DisciplineCode } from './types'
import type { AssignmentType, Grade } from './enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { gradeOptions } from '@shared/lib/format'

import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplatesApi } from '../../api/assignment-templates/list.hook'
import { useCurriculumModulesApi } from '../../api/curriculum/modules.hook'
import {
  assignmentTypeOptions,
  disciplineCodeOptions,
  getModuleOptions,
} from './assignment-template-options'

const SEARCH_DEBOUNCE_MS = 400

export const MODULE_LOCKED_PLACEHOLDER = 'Pick a grade and discipline'

interface FiltersAssignmentTemplatesReturn {
  refSearch: WritableComputedRef<string>
  refType: WritableComputedRef<AssignmentType | undefined>
  refDisciplineCode: WritableComputedRef<DisciplineCode | undefined>
  refGrade: WritableComputedRef<Grade | undefined>
  refModuleId: WritableComputedRef<number | undefined>
  refModuleOptions: ComputedRef<AppSelectOption<number>[]>
  /** The module catalogue is per grade + discipline, so the select is locked until both are set. */
  refIsModuleLocked: ComputedRef<boolean>
  refIsModulesLoading: Ref<boolean>
  refModulePlaceholder: ComputedRef<string>
  refCanManage: ComputedRef<boolean>
  refIsLoading: ComputedRef<boolean>
  refHasFilters: ComputedRef<boolean>
  typeOptions: AppSelectOption<AssignmentType>[]
  disciplineOptions: AppSelectOption<DisciplineCode>[]
  gradeSelectOptions: AppSelectOption<Grade>[]
  openCreateModal: () => void
  clearFilters: () => Promise<void>
}

export const useFiltersAssignmentTemplates = (): FiltersAssignmentTemplatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useAssignmentTemplatesStore()
  const { canManageAssignmentTemplates } = useSidebarPrivileges()

  const refModules = ref<CurriculumModuleOption[]>([])
  const refIsModulesLoading = ref<boolean>(false)
  /** The input as typed. The store holds the trimmed value, which a `v-model` bound straight to it
   *  would read back mid-word and swallow the space the admin just typed. */
  const refSearchInput = ref<string>(store.getFilterSearch ?? '')

  /** `page` is dropped so a new filter never lands on a page that no longer exists. */
  const syncQueryAndReload = async (): Promise<void> => {
    store.setCurrentPage(1)

    await router.push({
      query: {
        ...route.query,
        search: store.getFilterSearch || undefined,
        type: store.getFilterType || undefined,
        discipline_code: store.getFilterDisciplineCode || undefined,
        grade: store.getFilterGrade || undefined,
        curriculum_module_id: store.getFilterModuleId || undefined,
        page: undefined,
      },
    })
    await useAssignmentTemplatesApi()
  }

  const applySearch = useDebounceFn(() => syncQueryAndReload(), SEARCH_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => refSearchInput.value,
    set: (value) => {
      refSearchInput.value = value
      store.setFilterSearch(value.trim() || null)
      void applySearch()
    },
  })

  const refType = computed<AssignmentType | undefined>({
    get: () => store.getFilterType ?? undefined,
    set: (value) => {
      store.setFilterType(value ?? null)
      void syncQueryAndReload()
    },
  })

  // Module ids do not survive a grade or discipline change, so both setters drop the module.
  const refDisciplineCode = computed<DisciplineCode | undefined>({
    get: () => store.getFilterDisciplineCode ?? undefined,
    set: (value) => {
      store.setFilterDisciplineCode(value ?? null)
      store.setFilterModuleId(null)
      void syncQueryAndReload()
    },
  })

  const refGrade = computed<Grade | undefined>({
    get: () => store.getFilterGrade ?? undefined,
    set: (value) => {
      store.setFilterGrade(value ?? null)
      store.setFilterModuleId(null)
      void syncQueryAndReload()
    },
  })

  const refModuleId = computed<number | undefined>({
    get: () => store.getFilterModuleId ?? undefined,
    set: (value) => {
      store.setFilterModuleId(value ?? null)
      void syncQueryAndReload()
    },
  })

  const refIsModuleLocked = computed<boolean>(() =>
    store.getFilterGrade === null || store.getFilterDisciplineCode === null)

  const refModuleOptions = computed<AppSelectOption<number>[]>(() => getModuleOptions(refModules.value))

  const refModulePlaceholder = computed<string>(() =>
    refIsModuleLocked.value ? MODULE_LOCKED_PLACEHOLDER : 'All modules')

  const refCanManage = computed<boolean>(() => canManageAssignmentTemplates.value)
  const refIsLoading = computed<boolean>(() => store.isLoading)
  const refHasFilters = computed<boolean>(() => store.hasFilters)

  const loadModules = async (): Promise<void> => {
    const grade = store.getFilterGrade
    const disciplineCode = store.getFilterDisciplineCode

    refModules.value = []

    if (!grade || !disciplineCode) return

    const modules = await useCurriculumModulesApi({
      disciplineCode,
      grade,
      onStart: () => { refIsModulesLoading.value = true },
      onFinish: () => { refIsModulesLoading.value = false },
    })

    // The pair may have moved on while the request was in flight; only the current pair's answer lands.
    if (!modules || grade !== store.getFilterGrade || disciplineCode !== store.getFilterDisciplineCode) return

    refModules.value = modules
  }

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  const clearFilters = async (): Promise<void> => {
    store.resetFilters()
    await syncQueryAndReload()
  }

  watch(
    [() => store.getFilterGrade, () => store.getFilterDisciplineCode],
    () => { void loadModules() },
    { immediate: true },
  )

  // Back/forward and Clear rewrite the store behind the input; follow them unless the input already
  // agrees once trimmed, so typing is never fought.
  watch(() => store.getFilterSearch, (value) => {
    if ((value ?? '') !== refSearchInput.value.trim()) {
      refSearchInput.value = value ?? ''
    }
  })

  return {
    refSearch,
    refType,
    refDisciplineCode,
    refGrade,
    refModuleId,
    refModuleOptions,
    refIsModuleLocked,
    refIsModulesLoading,
    refModulePlaceholder,
    refCanManage,
    refIsLoading,
    refHasFilters,
    typeOptions: assignmentTypeOptions,
    disciplineOptions: disciplineCodeOptions,
    gradeSelectOptions: gradeOptions,
    openCreateModal,
    clearFilters,
  }
}
